import cx from 'classnames';
import { ReactNode } from 'react';
import styles from '../sass/components/Button.module.scss';
import Loader from './Loader';

interface ButtonProps {
  onClick?: () => void;
  size?: 'small' | 'large';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'icon' | 'link',
  children?: ReactNode;
  loading?: boolean;
  inactive?: boolean;
  /**
   * Looks inactive and says so to assistive tech (aria-disabled), but stays
   * focusable and clickable, so the click handler can explain what is
   * missing instead of a silent grey button (QA 2026-09-25 #2).
   */
  softDisabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  error?: boolean;
  /** Accessible name, for an icon-only button (passed to the <button>). */
  'aria-label'?: string;
  /** A toggle's state (e.g. the password "Show" button). */
  'aria-pressed'?: boolean;
  /** The element the button acts on. */
  'aria-controls'?: string;
  /** A disclosure button's state. */
  'aria-expanded'?: boolean;
  /** What explains the button (e.g. why it cannot book yet). */
  'aria-describedby'?: string;
}

interface ButtonClusterProps {
  children?: ReactNode;
}

export function ButtonCluster({ children }: ButtonClusterProps) {
  return <div className={styles.buttonGroup}>{children}</div>;
}

function Button({
  onClick,
  size = 'small',
  variant = 'primary',
  error = false,
  children,
  loading,
  inactive,
  softDisabled = false,
  type = 'button',
  className,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  'aria-controls': ariaControls,
  'aria-describedby': ariaDescribedBy,
  'aria-expanded': ariaExpanded,
}: ButtonProps) {
  return (
    <button
      className={cx(
        styles.button,
        styles[variant],
        styles[size],
        {
          [styles.inactive]: inactive || softDisabled,
          [styles.error]: error,
        },
        'label',
        className,
      )}
      // eslint-disable-next-line react/button-has-type
      type={type}
      onClick={onClick}
      disabled={inactive}
      aria-disabled={softDisabled && !inactive ? true : undefined}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      aria-controls={ariaControls}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
    >
      {loading && <Loader />}
      {children}
    </button>
  );
}

export default Button;
